import React, { useRef, useState } from "react";
import arrowDownIcon from "@src/assets/svg/arrow-down.svg";
import { useClickOutsideListener } from "@src/hooks/use-click-outside-listener";
import styles from "@src/components/atoms/ticket-routing-dropdown/ticket-routing-dropdown.module.scss";
import { ToolTip } from "@src/components/atoms/tooltip/tooltip";

const cssClassName = "ticket-routing-dropdown";

export interface TicketRoutingOption {
  value: string;
  label: string;
}

interface TicketRoutingDropdownProps {
  id: string;
  ariaLabel: string;
  value: string;
  options: TicketRoutingOption[];
  placeholder: string;
  disabled: boolean;
  disabledTooltip?: string;
  onChange: (value: string) => void;
}

const TicketRoutingDropdown = ({
  id,
  ariaLabel,
  value,
  options,
  placeholder,
  disabled,
  disabledTooltip,
  onChange,
}: TicketRoutingDropdownProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const listId = `${id}-options`;
  const selectedOption = options.find((option) => option.value === value);

  useClickOutsideListener(dropdownRef, () => setIsOpen(false), isOpen);

  const toggleOpen = () => {
    setIsOpen((open) => !open);
  };

  const dropdown = (
    <div
      className={[
        styles[cssClassName],
        isOpen && styles[`${cssClassName}--open`],
      ]
        .filter(Boolean)
        .join(" ")}
      ref={dropdownRef}
    >
      <button
        aria-controls={listId}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={ariaLabel}
        className={[
          styles[`${cssClassName}__trigger`],
          !disabled && styles[`${cssClassName}__trigger--active`],
        ]
          .filter(Boolean)
          .join(" ")}
        disabled={disabled}
        onClick={toggleOpen}
        role="combobox"
        type="button"
      >
        <span
          className={
            value
              ? styles[`${cssClassName}__value`]
              : styles[`${cssClassName}__placeholder`]
          }
        >
          {selectedOption?.label || placeholder}
        </span>
        <img alt="" aria-hidden="true" src={arrowDownIcon} />
      </button>
      {isOpen && !disabled && (
        <ul
          className={styles[`${cssClassName}__list`]}
          id={listId}
          role="listbox"
          style={{ maxHeight: 70 }}
        >
          {options.map((option) => (
            <li key={option.value} role="presentation">
              <button
                aria-selected={value === option.value}
                className={styles[`${cssClassName}__option`]}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                role="option"
                type="button"
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  return disabled && disabledTooltip ? (
    <ToolTip content={disabledTooltip} position="bottom">
      {dropdown}
    </ToolTip>
  ) : (
    dropdown
  );
};

export default TicketRoutingDropdown;
